import React, { useState, useEffect } from 'react';
import { Cpu, Zap, Terminal, Database, Cloud, AlertCircle, CheckCircle, Loader2, Code, Copy, Check, RefreshCw, GitCompare } from 'lucide-react';
import { API_CONFIG } from '../config/api';

interface TestResult {
  model: string;
  success: boolean;
  response?: string;
  error?: string;
  duration_ms?: number;
}

interface ConnectionStatus {
  status: string;
  workerUrl: string;
  environment: {
    hasAI: boolean;
    hasDB: boolean;
    hasCache: boolean;
    hasStorage: boolean;
  };
  availableModels: string[];
}

export default function AIConnectionTest() {
  const [status, setStatus] = useState<ConnectionStatus | null>(null);
  const [selectedModel, setSelectedModel] = useState('@cf/meta/llama-2-7b-chat-int8');
  const [prompt, setPrompt] = useState('Explain quantum computing in simple terms.');
  const [maxTokens, setMaxTokens] = useState(500);
  const [temperature, setTemperature] = useState(0.7);
  const [isLoading, setIsLoading] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [compareModels, setCompareModels] = useState<string[]>([]);
  const [compareResults, setCompareResults] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'single' | 'compare' | 'chain' | 'debug'>('single');
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [chainSteps, setChainSteps] = useState([
    {
      name: 'Analysis',
      model: '@cf/meta/llama-2-7b-chat-int8',
      promptTemplate: 'Analyze this topic and list 3 key points: quantum computing',
      max_tokens: 200
    },
    {
      name: 'Expansion',
      model: '@cf/meta/llama-2-7b-chat-int8',
      promptTemplate: 'Based on these points: {previous}\n\nProvide a detailed explanation.',
      max_tokens: 300
    }
  ]);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await fetch(`${API_CONFIG.API_URL}/ai-connection-test/status`);
      const data = await response.json();
      setStatus(data);
    } catch (error) {
      console.error('Error fetching status:', error);
    }
  };

  const testModel = async () => {
    setIsLoading(true);
    setTestResult(null);
    
    try {
      const response = await fetch(`${API_CONFIG.API_URL}/ai-connection-test/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: selectedModel,
          prompt,
          max_tokens: maxTokens,
          temperature
        })
      });
      
      const data = await response.json();
      setTestResult(data);
    } catch (error) {
      setTestResult({ success: false, error: 'Failed to connect to API' });
    } finally {
      setIsLoading(false);
    }
  };

  const runComparison = async () => {
    if (compareModels.length === 0) {
      alert('Please select at least one model to compare');
      return;
    }
    
    setIsLoading(true);
    setCompareResults(null);
    
    try {
      const response = await fetch(`${API_CONFIG.API_URL}/ai-connection-test/compare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          models: compareModels,
          prompt,
          max_tokens: maxTokens
        })
      });
      
      const data = await response.json();
      setCompareResults(data);
    } catch (error) {
      setCompareResults({ error: 'Failed to run comparison' });
    } finally {
      setIsLoading(false);
    }
  };

  const runChain = async () => {
    setIsLoading(true);
    setTestResult(null);
    
    try {
      const response = await fetch(`${API_CONFIG.API_URL}/ai-connection-test/chain`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ steps: chainSteps })
      });
      
      const data = await response.json();
      setTestResult(data);
    } catch (error) {
      setTestResult({ error: 'Failed to run chain' });
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const generateCode = () => {
    return `// Cloudflare Workers AI Example
const response = await env.AI.run('${selectedModel}', {
  prompt: \`${prompt}\`,
  max_tokens: ${maxTokens},
  temperature: ${temperature}
});

console.log(response.response);`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 text-white p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
            <Cpu className="w-10 h-10 text-purple-400" />
            AI Connection Test
          </h1>
          <p className="text-gray-300">Test and debug Cloudflare Workers AI bindings</p>
        </div>

        {/* Connection Status */}
        {status && (
          <div className="bg-gray-800/50 backdrop-blur rounded-xl p-6 mb-8 border border-purple-500/30">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                {status.status === 'connected' ? (
                  <CheckCircle className="w-6 h-6 text-green-400" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-red-400" />
                )}
                Connection Status
              </h2>
              <button
                onClick={fetchStatus}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-gray-700/50 rounded-lg p-3">
                <div className="text-sm text-gray-400 mb-1">Worker URL</div>
                <div className="font-mono text-sm">{status.workerUrl}</div>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-3">
                <div className="text-sm text-gray-400 mb-1">AI Binding</div>
                <div className="flex items-center gap-2">
                  {status.environment.hasAI ? (
                    <CheckCircle className="w-4 h-4 text-green-400" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-red-400" />
                  )}
                  {status.environment.hasAI ? 'Connected' : 'Not Found'}
                </div>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-3">
                <div className="text-sm text-gray-400 mb-1">Database</div>
                <div className="flex items-center gap-2">
                  {status.environment.hasDB ? (
                    <Database className="w-4 h-4 text-green-400" />
                  ) : (
                    <Database className="w-4 h-4 text-gray-500" />
                  )}
                  {status.environment.hasDB ? 'D1 Ready' : 'Not Connected'}
                </div>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-3">
                <div className="text-sm text-gray-400 mb-1">Storage</div>
                <div className="flex items-center gap-2">
                  {status.environment.hasStorage ? (
                    <Cloud className="w-4 h-4 text-green-400" />
                  ) : (
                    <Cloud className="w-4 h-4 text-gray-500" />
                  )}
                  {status.environment.hasStorage ? 'R2 Ready' : 'Not Connected'}
                </div>
              </div>
            </div>

            <div className="text-sm text-gray-400">
              Available Models: {status.availableModels.length}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {(['single', 'compare', 'chain', 'debug'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg transition-colors ${
                activeTab === tab
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-800/50 text-gray-300 hover:bg-gray-700/50'
              }`}
            >
              {tab === 'single' && 'Single Model'}
              {tab === 'compare' && 'Compare Models'}
              {tab === 'chain' && 'Chain Prompts'}
              {tab === 'debug' && 'Debug Info'}
            </button>
          ))}
        </div>

        {/* Single Model Test */}
        {activeTab === 'single' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-gray-800/50 backdrop-blur rounded-xl p-6 border border-purple-500/30">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Terminal className="w-5 h-5" />
                Test Configuration
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Model</label>
                  <select
                    value={selectedModel}
                    onChange={(e) => setSelectedModel(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:border-purple-500 focus:outline-none"
                  >
                    {status?.availableModels.map(model => (
                      <option key={model} value={model}>{model}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Prompt</label>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:border-purple-500 focus:outline-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Max Tokens</label>
                    <input
                      type="number"
                      value={maxTokens}
                      onChange={(e) => setMaxTokens(Number(e.target.value))}
                      className="w-full px-3 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:border-purple-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Temperature</label>
                    <input
                      type="number"
                      step="0.1"
                      value={temperature}
                      onChange={(e) => setTemperature(Number(e.target.value))}
                      className="w-full px-3 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:border-purple-500 focus:outline-none"
                    />
                  </div>
                </div>

                <button
                  onClick={testModel}
                  disabled={isLoading}
                  className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Zap className="w-5 h-5" />
                      Run Test
                    </>
                  )}
                </button>
              </div>

              {/* Code Example */}
              <div className="mt-6">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="text-sm font-semibold text-gray-400">Code Example</h4>
                  <button
                    onClick={() => copyToClipboard(generateCode(), 'code')}
                    className="p-1 hover:bg-gray-700 rounded transition-colors"
                  >
                    {copiedCode === 'code' ? (
                      <Check className="w-4 h-4 text-green-400" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                </div>
                <pre className="bg-gray-900 rounded-lg p-3 text-xs overflow-x-auto">
                  <code className="text-purple-300">{generateCode()}</code>
                </pre>
              </div>
            </div>

            {/* Test Result */}
            <div className="bg-gray-800/50 backdrop-blur rounded-xl p-6 border border-purple-500/30">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Code className="w-5 h-5" />
                Response
              </h3>

              {testResult ? (
                <div className="space-y-4">
                  {testResult.success ? (
                    <>
                      <div className="bg-gray-700/50 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-sm text-gray-400">Output</span>
                          {testResult.metadata && (
                            <span className="text-xs text-purple-400">
                              {testResult.metadata.duration_ms}ms
                            </span>
                          )}
                        </div>
                        <div className="whitespace-pre-wrap text-sm">
                          {testResult.response}
                        </div>
                      </div>
                      
                      {testResult.metadata && (
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="bg-gray-700/50 rounded p-2">
                            <span className="text-gray-400">Duration: </span>
                            <span className="text-purple-400">{testResult.metadata.duration_ms}ms</span>
                          </div>
                          <div className="bg-gray-700/50 rounded p-2">
                            <span className="text-gray-400">Tokens: </span>
                            <span className="text-purple-400">{testResult.metadata.max_tokens}</span>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4">
                      <div className="text-red-400 font-semibold mb-2">Error</div>
                      <div className="text-sm">{testResult.error}</div>
                      {testResult.details && (
                        <pre className="mt-2 text-xs text-gray-400">{testResult.details}</pre>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-12">
                  Run a test to see the response
                </div>
              )}
            </div>
          </div>
        )}

        {/* Compare Models */}
        {activeTab === 'compare' && (
          <div className="space-y-6">
            <div className="bg-gray-800/50 backdrop-blur rounded-xl p-6 border border-purple-500/30">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <GitCompare className="w-5 h-5" />
                Select Models to Compare
              </h3>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                {status?.availableModels.filter(m => !m.includes('whisper') && !m.includes('stable-diffusion')).map(model => (
                  <label key={model} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={compareModels.includes(model)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setCompareModels([...compareModels, model]);
                        } else {
                          setCompareModels(compareModels.filter(m => m !== model));
                        }
                      }}
                      className="rounded border-gray-600 bg-gray-700 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm">{model.split('/').pop()}</span>
                  </label>
                ))}
              </div>

              <button
                onClick={runComparison}
                disabled={isLoading || compareModels.length === 0}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Comparing...
                  </>
                ) : (
                  <>
                    <GitCompare className="w-5 h-5" />
                    Compare Models
                  </>
                )}
              </button>
            </div>

            {compareResults && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {compareResults.results?.map((result: any, index: number) => (
                  <div key={index} className="bg-gray-800/50 backdrop-blur rounded-xl p-4 border border-purple-500/30">
                    <div className="flex justify-between items-start mb-3">
                      <h4 className="text-sm font-semibold text-purple-400">
                        {result.model.split('/').pop()}
                      </h4>
                      {result.success ? (
                        <span className="text-xs text-green-400">{result.duration_ms}ms</span>
                      ) : (
                        <span className="text-xs text-red-400">Failed</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-300 max-h-48 overflow-y-auto">
                      {result.success ? result.response : result.error}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Chain Prompts */}
        {activeTab === 'chain' && (
          <div className="space-y-6">
            <div className="bg-gray-800/50 backdrop-blur rounded-xl p-6 border border-purple-500/30">
              <h3 className="text-lg font-semibold mb-4">Chain Configuration</h3>
              
              {chainSteps.map((step, index) => (
                <div key={index} className="mb-4 p-4 bg-gray-700/50 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input
                      placeholder="Step name"
                      value={step.name}
                      onChange={(e) => {
                        const newSteps = [...chainSteps];
                        newSteps[index].name = e.target.value;
                        setChainSteps(newSteps);
                      }}
                      className="px-3 py-2 bg-gray-800 rounded border border-gray-600 focus:border-purple-500 focus:outline-none"
                    />
                    <select
                      value={step.model}
                      onChange={(e) => {
                        const newSteps = [...chainSteps];
                        newSteps[index].model = e.target.value;
                        setChainSteps(newSteps);
                      }}
                      className="px-3 py-2 bg-gray-800 rounded border border-gray-600 focus:border-purple-500 focus:outline-none"
                    >
                      {status?.availableModels.filter(m => !m.includes('whisper') && !m.includes('stable-diffusion')).map(model => (
                        <option key={model} value={model}>{model}</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      placeholder="Max tokens"
                      value={step.max_tokens}
                      onChange={(e) => {
                        const newSteps = [...chainSteps];
                        newSteps[index].max_tokens = Number(e.target.value);
                        setChainSteps(newSteps);
                      }}
                      className="px-3 py-2 bg-gray-800 rounded border border-gray-600 focus:border-purple-500 focus:outline-none"
                    />
                  </div>
                  <textarea
                    placeholder="Prompt template (use {previous} for previous output)"
                    value={step.promptTemplate}
                    onChange={(e) => {
                      const newSteps = [...chainSteps];
                      newSteps[index].promptTemplate = e.target.value;
                      setChainSteps(newSteps);
                    }}
                    rows={2}
                    className="mt-2 w-full px-3 py-2 bg-gray-800 rounded border border-gray-600 focus:border-purple-500 focus:outline-none"
                  />
                </div>
              ))}

              <button
                onClick={runChain}
                disabled={isLoading}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Running Chain...
                  </>
                ) : (
                  <>
                    <Zap className="w-5 h-5" />
                    Run Chain
                  </>
                )}
              </button>
            </div>

            {testResult?.chain && (
              <div className="space-y-4">
                {testResult.chain.map((step: any, index: number) => (
                  <div key={index} className="bg-gray-800/50 backdrop-blur rounded-xl p-4 border border-purple-500/30">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-semibold text-purple-400">{step.step}</h4>
                      {step.success && (
                        <span className="text-xs text-green-400">{step.duration_ms}ms</span>
                      )}
                    </div>
                    <div className="text-sm text-gray-400 mb-2">Model: {step.model}</div>
                    <div className="bg-gray-900 rounded p-3 text-xs mb-2">
                      <div className="text-gray-500 mb-1">Prompt:</div>
                      <div className="whitespace-pre-wrap">{step.prompt}</div>
                    </div>
                    {step.success ? (
                      <div className="bg-gray-700/50 rounded p-3 text-sm">
                        {step.response}
                      </div>
                    ) : (
                      <div className="text-red-400 text-sm">{step.error}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Debug Info */}
        {activeTab === 'debug' && (
          <div className="bg-gray-800/50 backdrop-blur rounded-xl p-6 border border-purple-500/30">
            <h3 className="text-lg font-semibold mb-4">Debug Information</h3>
            <pre className="bg-gray-900 rounded-lg p-4 text-xs overflow-x-auto">
              <code className="text-gray-300">
                {JSON.stringify(status, null, 2)}
              </code>
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}